import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { TrendingUp, FileText, Calendar, AlertCircle } from 'lucide-react';
import { dashboardAPI } from '../utils/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    offene_gutachten: 0,
    abgeschlossene_gutachten: 0,
    termine_diese_woche: 0,
    neue_anfragen: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await dashboardAPI.getStats();
        setStats(data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
        if (err.response && err.response.status === 401) {
          localStorage.removeItem('autogutachter24_user');
          localStorage.removeItem('autogutachter24_token');
          navigate('/');
        }
      } finally {
        setLoading(false);
      }
    }
    
    fetchStats();
  }, [navigate]);

  const statCards = [
    {
      title: 'Offene Gutachten',
      value: stats.offene_gutachten,
      icon: FileText,
      iconColor: 'text-blue-500'
    },
    {
      title: 'Abgeschlossene Gutachten',
      value: stats.abgeschlossene_gutachten,
      icon: FileText,
      iconColor: 'text-green-500'
    },
    {
      title: 'Termine diese Woche',
      value: stats.termine_diese_woche,
      icon: Calendar,
      iconColor: 'text-blue-500'
    },
    {
      title: 'Neue Anfragen',
      value: stats.neue_anfragen,
      icon: AlertCircle,
      iconColor: 'text-orange-500'
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Welcome Header - Clean & Simple */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Willkommen zurück</h1>
          <p className="text-gray-500">Hier ist Ihre Übersicht für heute</p>
        </div>

        {/* Stats Grid - Minimalist Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-lg p-6 border border-gray-200 hover:border-blue-300 transition-colors"
              >
                <div className="flex items-center justify-between mb-4">
                  <Icon className={`w-5 h-5 ${stat.iconColor}`} />
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-2">{stat.title}</p>
                  <p className="text-3xl font-semibold text-gray-900">{loading ? '...' : stat.value}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent Activity - Clean Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-400" />
                Letzte Gutachten
              </h2>
            </div>
            <div className="p-6">
              <div className="divide-y divide-gray-100">
                {[1, 2, 3, 4].map((item) => (
                  <div key={item} className="flex items-center justify-between py-4 hover:bg-gray-50 px-4 -mx-4 cursor-pointer transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="text-sm font-medium text-gray-400">
                        #{1000 + item}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 text-sm">BMW 3er</div>
                        <div className="text-xs text-gray-500">Unfallschaden</div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">Vor {item}d</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-400" />
                Bevorstehende Termine
              </h2>
            </div>
            <div className="p-6">
              <div className="divide-y divide-gray-100">
                {[1, 2, 3, 4].map((item) => (
                  <div key={item} className="flex items-center justify-between py-4 hover:bg-gray-50 px-4 -mx-4 cursor-pointer transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="text-sm font-medium text-gray-400">
                        {10 + item}.12
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Besichtigung</div>
                        <div className="text-xs text-gray-500">Mercedes C-Klasse</div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">14:00</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;